<script lang="ts" setup>

import { useTheme } from 'vuetify'
import ScrollToTop from '@core/components/ScrollToTop.vue'
import { useThemeConfig } from '@core/composable/useThemeConfig'
import { hexToRgb } from '@layouts/utils'
import { Capacitor } from '@capacitor/core'
import eventListenerService from "@/events";
import Loader from './layouts/components/LoaderScreen.vue' //required, if removed, the loader will not work even-though vite says so.
import '@/styles/barcode-scanner.css'
import '@/styles/safe-area-notch.css'
import { StatusBar, Style } from '@capacitor/status-bar';
import { useAuthStore } from "@/stores/auth";
import { useUserStore } from "@/stores/user";
import fcm from "@/events/fcm";
import { PushNotifications } from '@capacitor/push-notifications';
import { useRouter } from 'vue-router';
import { useStatusPage } from '@/composables/useStatusPage'

import { useAppStore } from "@/stores/appStore";
import { storeToRefs } from "pinia";
import ReleaseCarousel from "@/components/ReleaseCarousel.vue";
import { useReleaseNoteStore } from "@/stores/releaseNoteStore";
import { usePermissionsStore } from "@/stores/permissionsStore";
import { usePermissionsMercure } from "@/composables/usePermissionsMercure";
import { App as CapacitorApp } from "@capacitor/app";
import ShareDestinationPicker from "@/components/ShareDestinationPicker.vue";
import { useShareReceiverStore } from "@/stores/shareReceiver";
import { useAppUpdate } from "@/composables/useAppUpdate";
import AppUpdateDialog from "@/components/AppUpdateDialog.vue";
import AccountNotLinkedOverlay from "@/components/AccountNotLinkedOverlay.vue";

const {
  syncInitialLoaderTheme,
  syncVuetifyThemeWithTheme: syncConfigThemeWithVuetifyTheme,
  isAppRtl,
  handleSkinChanges,
} = useThemeConfig()
const { global } = useTheme()
const router = useRouter();

const appStore = useAppStore();
const { snackbar, snackbarText, snackbarTimeout, snackColor, snackClose, snackIcon, loading, snackIconColor } = storeToRefs(appStore);

// DEVAMS-2156: iOS Share Sheet receiver — listen for the share-callback URL scheme.
const shareReceiverStore = useShareReceiverStore();

// DEVAMS-2164: iOS in-app update prompt
const { checkForUpdate } = useAppUpdate()

// ℹ️ Sync current theme with initial loader theme
syncInitialLoaderTheme()
syncConfigThemeWithVuetifyTheme()
handleSkinChanges()

eventListenerService.initialize()

// ℹ️ on mount actions:
onMounted(async () => {
  const device = Capacitor.getPlatform();
  console.log("Capacitor platform is: " + device);
  if (Capacitor.isNativePlatform()) {
    await StatusBar.setOverlaysWebView({ overlay: false });
    await StatusBar.setStyle({ style: Style.Default });
    await StatusBar.setBackgroundColor({ color: '#F7F7F9' }); // no switching when dark mode is active #282A42 to be build
  }

  // ℹ️ on mounted reload user data if authenticated
  const authStore = useAuthStore()
  if (authStore.getIsLoggedInState) {
    const userStore = useUserStore()
    const releaseNoteStore = useReleaseNoteStore()
    await userStore.renewData()
    releaseNoteStore.fetchUnseen()
    fcm.register();

    // DEVAMS-2025: permission load is now driven by the router's beforeEach guard
    // (router/index.js) so that any page mounting checkAccessPage() at <script setup>
    // time already has the store populated. We only start the Mercure subscription
    // here. The store will already be loaded by the time we reach this point on
    // any post-login navigation.
    const permissionsStore = usePermissionsStore();
    const permSub = usePermissionsMercure();
    permSub.start();

    // Tear down the Mercure subscription on logout so we don't leave dangling
    // EventSource connections when the user signs out.
    emitter.on('logout', (_data: unknown) => {
      permSub.stop();
      permissionsStore.$reset();
      // DEVAMS-2656: reset the user store (incl. accountLinkError) on logout so a
      // stale "account isn't linked" overlay doesn't survive into the next login.
      userStore.$reset();
    });

    // Fallback: re-fetch on tab focus if the store is stale (>5 min).
    // This covers Mercure dropouts and backgrounded browser tabs.
    window.addEventListener('focus', () => {
      if (authStore.getIsLoggedInState && permissionsStore.isStale && !permissionsStore.loading) {
        permissionsStore.fetchPermissions();
      }
    });
  }
  if (Capacitor.getPlatform() !== "web") {
    PushNotifications.addListener('pushNotificationActionPerformed', (notification) => {
      console.debug('Push notification action performed:', notification);
      const plannedWorkId = notification.notification?.data?.plannedWorkId;
      if (plannedWorkId) {
        router.push({ path: `/task/${plannedWorkId}` });
      }
      // DEVAMS-2645: car checklist reminder/snooze-expiry push — deep link to the checklist.
      const carChecklistId = notification.notification?.data?.carChecklistId;
      if (carChecklistId) {
        router.push({ path: `/tooling/checklist/${carChecklistId}` });
      }
    });
  }

  // DEVAMS-2164: iOS in-app update prompt — check on cold start
  checkForUpdate()

  // DEVAMS-2156: iOS Share Sheet — three coverage paths:
  // 1. Cold start check: iOS often kills the App when the Share Extension runs.
  //    When the user taps Platcop after sharing, it's a fresh launch — appStateChange
  //    never fires. Check immediately on startup instead.
  // 2. appUrlOpen: if the responder chain URL open works, catch it here.
  // 3. appStateChange: background → foreground (app was not killed).
  if (Capacitor.getPlatform() !== 'web') {
    // Path 1 — cold start / killed-by-iOS scenario
    shareReceiverStore.loadPendingFile()

    // Path 2 — URL scheme open from Share Extension
    await CapacitorApp.addListener('appUrlOpen', ({ url }: { url: string }) => {
      if (url.startsWith('aero.nayak.mro.jerkee://share-callback')) {
        shareReceiverStore.loadPendingFile()
      }
    })

    // Path 3 — background → foreground (app was kept alive)
    // DEVAMS-2164: also re-check for updates on resume (single listener for both concerns)
    await CapacitorApp.addListener('appStateChange', ({ isActive }: { isActive: boolean }) => {
      if (isActive) {
        shareReceiverStore.loadPendingFile()
        checkForUpdate()
      }
    })
  }

  if (Capacitor.getPlatform() === 'web') {
    console.debug('[STATUSPAGE] trying to load: Status page script')
    useScriptTag(
        'https://platcop.statuspage.io/embed/script.js',
        // Callback when script loads (optional)
        () => {
          console.debug('[STATUSPAGE] Status page script loaded')
        },
        { async: true, attrs: { defer: 'true' } }
    )
  }
})

const handleSwipe = () => {
  const tag = document.activeElement?.tagName
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return
  if (document.activeElement?.getAttribute('contenteditable') === 'true') return
  console.debug('swiped right, going to previous page')
  if (router.options.history.state.back) {
    router.back();
  }
}

useStatusPage()
</script>

<template>
  <loader :view-on="loading" />
  <VLocaleProvider
    v-touch="{right: handleSwipe}"
    :rtl="isAppRtl"
  >
    <!-- ℹ️ This is required to set the background color of active nav link based on currently active global theme's primary -->
    <VApp
      id="main-app-bar"
      :style="`--v-global-theme-primary: ${hexToRgb(global.current.value.colors.primary)}`"
    >
      <RouterView />
      <ScrollToTop />
    </VApp>
    <v-snackbar
      v-model="snackbar"
      :color="snackColor"
      :timeout="snackbarTimeout"
    >
      <v-icon
        v-if="snackIcon"
        :icon="snackIcon"
        :style="{ color: snackIconColor + ' !important' }"
        start
      />
      {{ snackbarText }}
      <template
        v-if="snackClose"
        #actions
      >
        <v-btn
          color="blue"
          variant="text"
          @click="snackbar = false"
        >
          Close
        </v-btn>
      </template>
    </v-snackbar>
    <ReleaseCarousel />
    <!-- DEVAMS-2156: iOS Share Sheet destination picker (global bottom sheet) -->
    <ShareDestinationPicker />
    <!-- DEVAMS-2164: iOS in-app update prompt -->
    <AppUpdateDialog />
    <!-- DEVAMS-2448: non-dismissible overlay when the authenticated identity has no linked PCP account -->
    <AccountNotLinkedOverlay />
  </VLocaleProvider>
</template>
